import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import { createQueryParams } from "features/config/helpers";
import { NoticeModel } from "features/stock-ordering/core/domain/notice.model";
import {
  getAdminNotices,
  selectGetAdminNotices,
  resetGetAdminNotices,
  GetAdminNoticesState,
} from "../slices/get-admin-notices.slice";
import {
  createNotice,
  selectCreateNotice,
  resetCreateNotice,
  CreateNoticeState,
} from "../slices/create-notice.slice";
import {
  updateNotice,
  selectUpdateNotice,
  resetUpdateNotice,
  UpdateNoticeState,
} from "../slices/update-notice.slice";
import {
  deleteNotice,
  selectDeleteNotice,
  resetDeleteNotice,
  DeleteNoticeState,
} from "../slices/delete-notice.slice";
import { IoMdClose } from "react-icons/io";
import moment from "moment";

export function NoticesAdminContent() {
  const dispatch = useAppDispatch();
  const getAdminNoticesState = useAppSelector(selectGetAdminNotices);
  const createNoticeState = useAppSelector(selectCreateNotice);
  const updateNoticeState = useAppSelector(selectUpdateNotice);
  const deleteNoticeState = useAppSelector(selectDeleteNotice);

  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState<NoticeModel | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formBody, setFormBody] = useState("");
  const [formStatus, setFormStatus] = useState<number>(0);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const fetchNotices = () => {
    const query = createQueryParams({ admin: 1, limit: 50, page: 1 });
    dispatch(getAdminNotices(query));
  };

  useEffect(() => {
    fetchNotices();
  }, [dispatch]);

  useEffect(() => {
    if (createNoticeState.status === CreateNoticeState.success) {
      dispatch(resetCreateNotice());
      resetForm();
      fetchNotices();
    }
  }, [createNoticeState, dispatch]);

  useEffect(() => {
    if (updateNoticeState.status === UpdateNoticeState.success) {
      dispatch(resetUpdateNotice());
      resetForm();
      fetchNotices();
    }
  }, [updateNoticeState, dispatch]);

  useEffect(() => {
    if (deleteNoticeState.status === DeleteNoticeState.success) {
      dispatch(resetDeleteNotice());
      setConfirmDeleteId(null);
      fetchNotices();
    }
  }, [deleteNoticeState, dispatch]);

  const resetForm = () => {
    setShowForm(false);
    setEditingNotice(null);
    setFormTitle("");
    setFormBody("");
    setFormStatus(0);
  };

  const handleEdit = (notice: NoticeModel) => {
    setEditingNotice(notice);
    setFormTitle(notice.title);
    setFormBody(notice.body);
    setFormStatus(notice.status);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingNotice(null);
    setFormTitle("");
    setFormBody("");
    setFormStatus(0);
    setShowForm(true);
  };

  const handleSubmit = (publishNow: boolean) => {
    const status = publishNow ? 1 : formStatus;

    if (editingNotice) {
      dispatch(
        updateNotice({
          id: editingNotice.id,
          title: formTitle,
          body: formBody,
          status: status,
        })
      );
    } else {
      dispatch(
        createNotice({
          title: formTitle,
          body: formBody,
          status: status,
        })
      );
    }
  };

  const handleHide = (notice: NoticeModel) => {
    dispatch(
      updateNotice({
        id: notice.id,
        title: notice.title,
        body: notice.body,
        status: 0,
      })
    );
  };

  const handleDelete = (noticeId: number) => {
    dispatch(deleteNotice(noticeId));
  };

  const notices = getAdminNoticesState.data?.notices ?? [];
  const isLoading =
    getAdminNoticesState.status === GetAdminNoticesState.inProgress;
  const isSaving =
    createNoticeState.status === CreateNoticeState.inProgress ||
    updateNoticeState.status === UpdateNoticeState.inProgress;

  return (
    <div className="space-y-4 font-[Roboto]">
      <div className="flex flex-col px-4 lg:flex-row lg:items-end">
        <span className="text-secondary text-3xl font-['Bebas_Neue'] flex-1">
          Notices Management
        </span>
        <button
          onClick={handleCreate}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-md"
        >
          + Create Notice
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-red-600 to-orange-600">
            <h3 className="text-white font-bold text-lg">
              {editingNotice ? "Edit Notice" : "Create Notice"}
            </h3>
            <button
              onClick={resetForm}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <IoMdClose size={24} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="Enter notice title..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Body
              </label>
              <textarea
                value={formBody}
                onChange={(e) => setFormBody(e.target.value)}
                placeholder="Enter notice content..."
                rows={10}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all resize-y"
              />
            </div>

            {editingNotice && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Status:
                </label>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    formStatus === 1
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {formStatus === 1 ? "Published" : "Draft"}
                </span>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSubmit(false)}
                disabled={!formTitle.trim() || !formBody.trim() || isSaving}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving..." : "Save as Draft"}
              </button>
              <button
                onClick={() => handleSubmit(true)}
                disabled={!formTitle.trim() || !formBody.trim() || isSaving}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Publishing..." : "Publish"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-3 font-semibold text-gray-600">
                  Title
                </th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">
                  Status
                </th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">
                  Published At
                </th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">
                  Created By
                </th>
                <th className="text-left px-6 py-3 font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    Loading notices...
                  </td>
                </tr>
              ) : notices.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    No notices found. Create your first notice.
                  </td>
                </tr>
              ) : (
                notices.map((notice) => (
                  <tr
                    key={notice.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-3">
                      <span className="font-medium text-gray-800">
                        {notice.title}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          notice.status === 1
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {notice.status === 1 ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      {notice.published_at
                        ? moment(notice.published_at).format(
                            "MMM D, YYYY h:mm A"
                          )
                        : "—"}
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      {notice.first_name} {notice.last_name}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(notice)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
                        >
                          Edit
                        </button>

                        {notice.status === 1 && (
                          <button
                            onClick={() => handleHide(notice)}
                            className="text-orange-600 hover:text-orange-800 font-medium text-sm transition-colors"
                          >
                            Hide
                          </button>
                        )}

                        {confirmDeleteId === notice.id ? (
                          <span className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">
                              Sure?
                            </span>
                            <button
                              onClick={() => handleDelete(notice.id)}
                              className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="text-gray-500 hover:text-gray-700 font-medium text-sm transition-colors"
                            >
                              No
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(notice.id)}
                            className="text-red-600 hover:text-red-800 font-medium text-sm transition-colors"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
